import React, { useCallback, useState } from 'react';
import { Box, Checkbox, FormControlLabel, Switch, Typography } from '@mui/material';
import { styles } from './PdfEnhancementPanel.styles';
import { ISetupPdfMessages } from '../../../../../types/setupPDFTypes';
import { AccordianSection } from '../../../SetupPDFComponents/AccordianSection/AccordianSection';
import { Watermark } from '../../../SetupPDFComponents/Watermark/Watermark';
import { IPageMargins, PageMargins, PageMarginsValue } from '../../../SetupPDFComponents/PageMargin/PageMargins';
import { IExportPdfOptionsState } from '../StorySetupPDFView';
import PageFootNotes from './PageFootNotes/PageFootNotes';

export interface IPdfEnhancementPanelProps {
  messages: ISetupPdfMessages;
  exportPdfOptions: IExportPdfOptionsState;
  setExportPdfOptions: (exportPdfOptions: IExportPdfOptionsState) => void;
}

export const PdfEnhancementPanel: React.FC<IPdfEnhancementPanelProps> = ({
  messages,
  exportPdfOptions,
  setExportPdfOptions
}) => {
  const [pageMargins, setPageMargins] = useState<IPageMargins | null>(
    exportPdfOptions.margins
      ? {
        top: exportPdfOptions.margins.marginTop?.toString() || '',
        bottom: exportPdfOptions.margins.marginBottom?.toString() || '',
        left: exportPdfOptions.margins.marginLeft?.toString() || '',
        right: exportPdfOptions.margins.marginRight?.toString() || ''
      }
      : null
  );
  const handleWatermarkChange = useCallback(
    (value: string | null) => {
      setExportPdfOptions({ ...exportPdfOptions, watermark: value });
    },
    [exportPdfOptions, setExportPdfOptions]
  );
  const handleEnlargeFontsChange = useCallback(
    (value: boolean) => {
      setExportPdfOptions({ ...exportPdfOptions, enlargeFonts: value });
    },
    [exportPdfOptions, setExportPdfOptions]
  );
  const handlePageMarginsChange = useCallback(
    (value: PageMarginsValue, customMargins: IPageMargins) => {
      if (value === PageMarginsValue.DEFAULT) {
        setPageMargins(null);
        setExportPdfOptions({ ...exportPdfOptions, margins: null, storyMarginsEnabled: false });
      } else {
        setPageMargins(customMargins);
        if (customMargins) {
          const { top, bottom, left, right } = customMargins;
          setExportPdfOptions({
            ...exportPdfOptions,
            margins: {
              marginTop: Number(top),
              marginBottom: Number(bottom),
              marginLeft: Number(left),
              marginRight: Number(right)
            },
            storyMarginsEnabled: true
          });
        }
      }
    },
    [exportPdfOptions, setExportPdfOptions]
  );

  return (
    <Box data-test="storySetupPdfEnhancementPanel" sx={styles.container}>
      <Box sx={styles.header}>
        <Typography variant="h3" sx={styles.headerTitle}>
          {messages.pdfStyling}
        </Typography>
      </Box>
      <Box sx={styles.content}>
        {/* Watermark Section */}
        <AccordianSection title={messages.watermark}>
          <Watermark
            messages={messages}
            watermark={exportPdfOptions.watermark}
            onWatermarkChange={handleWatermarkChange}
          />
        </AccordianSection>

        {/* Page Margins Section */}
        <AccordianSection title={messages.pageMargins}>
          <PageMargins messages={messages} pageMargins={pageMargins} onPageMarginsChange={handlePageMarginsChange} />
          <Box sx={styles.pdfScaleContainer}>
            <Typography variant="body1" sx={styles.pdfScaleLabel}>{messages.pdfScale}</Typography>
            <FormControlLabel
              control={
                <Checkbox checked={exportPdfOptions.enlargeFonts} onChange={(_, checked) => handleEnlargeFontsChange(checked)} name="enlargeFonts" />
              }
              label={messages.enlargeFonts}
            />
          </Box>
        </AccordianSection>

        {/* Header Controls Section */}
        <AccordianSection title={messages.headerControls}>
          <FormControlLabel
            sx={styles.switchControlLabel}
            control={<Switch size="small" />}
            label={
              <span
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                  }
                }}
                role="button"
                tabIndex={0}
              >
                {messages.showHeader}
              </span>
            }
            labelPlacement="start"
            checked={exportPdfOptions.showHeader}
            onChange={(_, checked) => {
              setExportPdfOptions({ ...exportPdfOptions, showHeader: checked });
            }}
          />
        </AccordianSection>

        {/* Footer Controls Section */}
        <AccordianSection title={messages.footerControls}>
          <FormControlLabel
            sx={styles.switchControlLabel}
            control={<Switch size="small" />}
            label={
              <span
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                  }
                }}
                role="button"
                tabIndex={0}
              >
                {messages.showFooter}
              </span>
            }
            labelPlacement="start"
            checked={exportPdfOptions.showFooter}
            onChange={(_, checked) => {
              setExportPdfOptions({ ...exportPdfOptions, showFooter: checked });
            }}
          />
        </AccordianSection>
        <AccordianSection title={messages.pageFootNotes}>
          <PageFootNotes
            messages={messages}
            exportPdfOptions={exportPdfOptions}
            setExportPdfOptions={setExportPdfOptions}
          />
        </AccordianSection>
      </Box>
    </Box>
  );
};

export default PdfEnhancementPanel;
